import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import { toast } from "sonner";
import { Check, X, Clock } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AttendancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(todayISO());
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [savingAttendance, setSavingAttendance] = useState(false);

  // Coach self-attendance
  const [myAttendance, setMyAttendance] = useState([]);
  const [submittingSelf, setSubmittingSelf] = useState(false);

  // Admin: pending coach attendances
  const [pendingCoach, setPendingCoach] = useState([]);
  const [adminPhone, setAdminPhone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ss, present, settings] = await Promise.all([
        api.listStudents(),
        api.getStudentAttendance(date),
        api.getSettings(),
      ]);
      setStudents(ss);
      setSelected(new Set(present.map((p) => p.student_id)));
      setAdminPhone(settings?.admin_phone || null);

      if (user?.role === "coach") {
        setMyAttendance(await api.myCoachAttendance());
      }
      if (user?.role === "admin") {
        try {
          setPendingCoach(await api.listCoachAttendance("pending"));
        } catch {
          setPendingCoach([]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [date, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const markAll = () => setSelected(new Set(students.map((s) => s.id)));
  const clearAll = () => setSelected(new Set());

  const saveAttendance = async () => {
    setSavingAttendance(true);
    try {
      await api.markStudentAttendance(date, Array.from(selected));
      toast.success("Attendance saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingAttendance(false);
    }
  };

  const markMyself = async () => {
    setSubmittingSelf(true);
    try {
      await api.markMyCoachAttendance(date);
      toast.success("Own attendance submitted for review");
      setMyAttendance(await api.myCoachAttendance());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingSelf(false);
    }
  };

  const review = async (id, status) => {
    try {
      await api.reviewCoachAttendance(id, status);
      toast.success(`Marked ${status}`);
      setPendingCoach(await api.listCoachAttendance("pending"));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const alreadyMarkedToday = myAttendance.some((a) => a.date === date);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Attendance</h1>
        <p className="text-slate-500 text-sm mt-1">Mark students & submit your own attendance for approval.</p>
      </div>

      <div className="flex items-end gap-3">
        <label className="block">
          <span className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Date</span>
          <input
            data-testid={TID.attendanceDate}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 px-3 border border-slate-200 rounded-lg bg-white"
          />
        </label>
      </div>

      {/* Coach self-attendance */}
      {user?.role === "coach" && (
        <section className="bg-slate-50 border border-slate-100 rounded-xl p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-slate-900">My Attendance ({date})</p>
              <p className="text-xs text-slate-500">
                {alreadyMarkedToday ? "Submitted · awaiting admin review" : "Submit your attendance for admin approval"}
              </p>
            </div>
            <button
              data-testid={TID.coachMarkOwn}
              onClick={markMyself}
              disabled={submittingSelf || alreadyMarkedToday}
              className="h-10 px-4 bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-50 text-white rounded-lg font-semibold text-sm"
            >
              {alreadyMarkedToday ? "Submitted" : submittingSelf ? "Submitting..." : "Mark my attendance"}
            </button>
          </div>
        </section>
      )}

      {/* Student attendance */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-900">Students Present</h2>
          {date >= todayISO() && (
            <div className="flex gap-2">
              <button
                data-testid={TID.attendanceMarkAll}
                onClick={markAll}
                className="text-xs font-bold px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50"
              >
                Select all
              </button>
              <button
                onClick={clearAll}
                className="text-xs font-bold px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading...</div>
        ) : students.length === 0 ? (
          <div className="text-center text-slate-500 py-10 bg-slate-50 rounded-xl border border-slate-100">
            No students available.
          </div>
        ) : date < todayISO() ? (
          (() => {
            const present = students
              .filter((s) => selected.has(s.id))
              .slice()
              .sort((a, b) => {
                const c = (a.coach_name || "~").localeCompare(b.coach_name || "~", undefined, { sensitivity: "base" });
                return c !== 0 ? c : (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
              });
            if (present.length === 0) {
              return (
                <div className="text-center text-slate-500 py-10 bg-slate-50 rounded-xl border border-slate-100">
                  No students were marked present on {date}.
                </div>
              );
            }
            return (
              <ol data-testid="attendance-past-list" className="space-y-1 bg-slate-50 rounded-xl border border-slate-100 p-4">
                {present.map((s, i) => (
                  <li key={s.id} className="flex items-baseline gap-2 text-sm">
                    <span className="text-slate-400 tabular-nums w-8 shrink-0">#{i + 1}</span>
                    <span className="font-semibold text-slate-900 truncate">{s.name}</span>
                    <span className="text-xs text-[#FF6B6B] font-semibold truncate">· {s.coach_name || "No coach"}</span>
                  </li>
                ))}
              </ol>
            );
          })()
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {students.map((s) => {
              const on = selected.has(s.id);
              return (
                <label
                  key={s.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    on ? "bg-[#0A2540]/5 border-[#0A2540]" : "bg-white border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    data-testid={TID.attendanceCheckbox(s.id)}
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(s.id)}
                    className="w-5 h-5 accent-[#0A2540]"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate text-sm">{s.name}</p>
                    <p className="text-xs text-slate-500 truncate">{s.phone_number}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {date >= todayISO() && (
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <button
              data-testid={TID.attendanceSubmit}
              onClick={saveAttendance}
              disabled={savingAttendance || students.length === 0}
              className="h-11 px-6 bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-60 text-white rounded-lg font-semibold text-sm"
            >
              {savingAttendance ? "Saving..." : `Confirm attendance (${selected.size} present)`}
            </button>
            {user?.role === "coach" && adminPhone && (
              <button
                data-testid="notify-admin-btn"
                type="button"
                disabled={selected.size === 0}
                onClick={() => {
                  const names = students
                    .filter((s) => selected.has(s.id))
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                    .map((s, i) => `${i + 1}. ${s.name}`)
                    .join("\n");
                  const msg = `Hi Admin, this is ${user?.full_name || "Coach"}. Attendance for ${date} — ${selected.size} student(s) present:\n${names}\n— Pathaans Academy Ludhiana`;
                  openWhatsApp(adminPhone, msg);
                }}
                className="h-11 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 font-semibold text-sm"
                title="Notify admin on WhatsApp"
              >
                Notify Admin
              </button>
            )}
          </div>
        )}
      </section>

      {/* Admin: review pending coach attendance */}
      {isAdmin && (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3">Pending Coach Attendance</h2>
          {pendingCoach.length === 0 ? (
            <div className="text-center text-slate-500 py-10 bg-slate-50 rounded-xl border border-slate-100">
              Nothing to review.
            </div>
          ) : (
            <div className="space-y-2">
              {pendingCoach.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3"
                >
                  <div className="w-10 h-10 rounded-full bg-[#FF6B6B] text-white flex items-center justify-center font-bold shrink-0">
                    {a.coach_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate">{a.coach_name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {a.date}
                    </p>
                  </div>
                  <button
                    data-testid={TID.coachReviewApprove(a.id)}
                    onClick={() => review(a.id, "approved")}
                    className="w-9 h-9 rounded-full text-[#34C759] hover:bg-[#34C75922] flex items-center justify-center"
                    title="Approve"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={TID.coachReviewReject(a.id)}
                    onClick={() => review(a.id, "rejected")}
                    className="w-9 h-9 rounded-full text-[#FF3B30] hover:bg-[#FF3B3022] flex items-center justify-center"
                    title="Reject"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Coach: my attendance history */}
      {user?.role === "coach" && myAttendance.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3">My Recent Attendance</h2>
          <div className="space-y-2">
            {myAttendance.slice(0, 10).map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{a.date}</p>
                </div>
                <span
                  className="text-xs font-bold uppercase px-2 py-0.5 rounded-full"
                  style={{
                    background:
                      a.status === "approved" ? "#34C75922" : a.status === "rejected" ? "#FF3B3022" : "#FF950022",
                    color:
                      a.status === "approved" ? "#0A8F3D" : a.status === "rejected" ? "#B4262B" : "#B36400",
                  }}
                >
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
